import { DOCUMENT, NgClass, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, Inject, Input, OnDestroy, OnInit, QueryList, signal, ViewChild, ViewChildren } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { Subscription } from "rxjs";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { SeoService } from "src/app/servicios/seo/seo.service";
import { AnimacionVideoComponent } from "../../animacion-video/animacion-video.component";
import { SkeletonImageComponent } from "../../skeleton-image/skeleton-image/skeleton-image.component";

declare let DM: any;

@Component({
  selector: "app-video-noticia",
  templateUrl: "./video-noticia.component.html",
  styleUrls: ["./video-noticia.component.css"],
  imports: [NgClass, AnimacionVideoComponent, SkeletonImageComponent, NgIf],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoNoticiaComponent implements OnDestroy, OnInit {
  @Input() set noticia(valor: any) {
    this.setNoticia(valor);
  }

  private _noticia;

  get noticia(): any {
    return this._noticia();
  }
  routingSub: Subscription;
  video: any;
  player: any;
  videoPausado = signal(true);
  videoCargando = signal(true);
  videoType: string;
  isMobile;
  showDivVideo = true;
  videoYoutube;
  customVideo;
  firstTimePlayYT = true;
  playerDM;
  playerVars;
  showPlayer = false;
  limiteVideo = false;
  counter: number = 0;

  @ViewChildren("dmPlayer") dmWrapper: QueryList<any>;
  @ViewChild("animacion") animacionComponent: AnimacionVideoComponent;
  @ViewChild("customVideo", { static: false }) customVideoDiv: ElementRef<HTMLVideoElement>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private platformService: PlatformService,
    private deviceService: DeviceDetectorService,
    private seoService: SeoService,
  ) {
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnInit(): void {
    this.detectarTipo();
    if (this.videoType === "youtube") {
      this.loadYoutubeAPI();
    }
  }

  ngAfterViewInit() {
    if (this.videoType === "dailymotion") {
      /* this.startDMPlayer(); */
    } else if (this.videoType === "custom") {
      this.seoService.insertaScriptVideo(this.videoType, this._noticia(), this.customVideoDiv);
    }
  }

  /**
   * La función "setNoticia" establece la noticia actual y, si es un video, carga la API de YouTube y el
   * reproductor de video de YouTube.
   * @param value - El parámetro "value" es un objeto que representa la noticia actual.
   */
  setNoticia(value: any): void {
    this._noticia = signal(value);
    this.detectarTipo();
    this.videoPausado.set(true);
    this.videoCargando.set(true);
    if (this.videoType === "youtube") {
      this.loadYoutubeAPI();
    } else if (this.videoType === "dailymotion") {
      this.loadDMAPI();
    } else {
      this.videoCargando.set(false);
    }
  }

  /**
   * La función verifica si la API de YouTube ya está cargada y, si no, carga dinámicamente la API e
   * inicializa el reproductor de YouTube.
   */
  loadYoutubeAPI() {
    if (this.platformService.isBrowser()) {
      if (window["YT"]) {
        this.startYTPlayer();
      } else {
        const tag = document.createElement("script");
        tag.defer = true;
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window["onYouTubeIframeAPIReady"] = this.startYTPlayer.bind(this);
      }
    }
  }

  /**
   * La función verifica si la API de YouTube ya está cargada y, si no, carga dinámicamente la API e
   * inicializa el reproductor de YouTube.
   */
  loadDMAPI() {
    if (this.platformService.isBrowser()) {
      if (window["DM"] || window["playerDM"]) {
        this.startDMPlayer();
      } else {
        const tag = document.createElement("script");
        tag.src = "https://api.dmcdn.net/all.js";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    }
  }

  /**
   * La función "startYTPlayer" inicializa y configura un reproductor de YouTube con configuraciones y
   * controladores de eventos específicos.
   */
  startYTPlayer() {
    if (this.platformService.isBrowser()) {
      if (window["player"] && window["player"].destroy) {
        window["player"].destroy();
      }
      window["player"] = new window["YT"].Player("player", {
        videoId: this.getYtId(this._noticia()?.urlVideo),
        height: "100%",
        width: "100%",
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          enablejsapi: 1,
          controls: 1,
          disablekb: 1,
          rel: 0,
          showinfo: 0,
          fs: 1,
          playsinline: 1,
          origin: window.location.href,
        },
        events: {
          onReady: () => this.onPlayerReady(),
        },
      });
    }
  }

  /**
   * La función "startDMPlayer" inicializa y configura un reproductor de Dailymotion con configuraciones.
   */
  startDMPlayer() {
    if (this.platformService.isBrowser()) {
      if (window["playerDM"] && window["playerDM"].nodeName === "IFRAME") {
        const wrapper = document.querySelector(".dmWrapper");
        const dmPlayer = document.querySelector(".dmPlayer");
        dmPlayer.remove();
        const div = this.document.createElement("div");
        div.className = "dmPlayer";
        wrapper.appendChild(div);
        window["playerDM"] = undefined;
      }
      const dmPlayer = document.querySelector(".dmPlayer");
      if (dmPlayer) {
        window["playerDM"] = DM.player(dmPlayer, {
          video: this.getDailyId(this._noticia()?.urlVideo),
          width: "100%",
          height: "100%",
          params: {
            controls: 1,
            autoplay: 0,
          },
        });
        if (this.platformService.isBrowser()) {
          setTimeout(() => {
            this.videoCargando.set(false);
          }, 150);
        }
      }
    }
  }

  /**
   * La función "onPlayerReady" carga un vídeo de YouTube mediante su ID y lo pausa.
   * @param event - El parámetro de evento es un objeto que representa el evento que activó la función
   * onPlayerReady.
   */
  onPlayerReady() {
    this.videoPausado.set(true);
    this.videoCargando.set(false);
    if (this.videoPausado && this.animacionComponent) {
      this.videoCargando.set(false);
      this.animacionComponent.setVideoCargando(false);
    }

    if (this.player) {
      this.player.loadVideoById(this.getYtId(this._noticia()?.urlVideo));
      this.seoService.insertaScriptVideo(this.videoType, this._noticia());
      this.player.pauseVideo();
    }
  }

  ngOnDestroy(): void {
    if (this.routingSub) this.routingSub.unsubscribe();
    if (this.platformService.isBrowser()) {
      if (window["onYouTubeIframeAPIReady"]) window["onYouTubeIframeAPIReady"] = null;
    }
    if (this.player) {
      this.pauseVideo();
      this.player.destroy();
    }
    if (this.platformService.isBrowser()) {
      if (window["YT"]) window["YT"] = null;
    }
  }

  /**
   * La función "esVideoYoutube" verifica si una URL contiene la cadena "youtube.com" y devuelve un valor
   * booleano.
   * @param url - La URL que se va a verificar.
   * @returns Un valor booleano. Devolverá verdadero si la URL contiene la cadena "youtube.com" y falso si
   * no la contiene.
   */
  esVideoYoutube(url): boolean {
    const regExp = /youtube.com/gi;
    if (url?.search(regExp) === -1) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * La función "esDailymotion" verifica si una URL contiene la cadena "dailymotion.com" y devuelve un valor
   * @param url - La URL que se va a verificar.
   * @returns {boolean} - Un booleano que indica si la URL contiene la cadena "dailymotion.com".
   */
  esDailymotion(url): boolean {
    const regExp = /dailymotion.com/gi;
    if (url?.search(regExp) === -1) {
      return false;
    } else {
      return true;
    }
  }

  stateChangeYoutube(data): void {
    if (data.target.getPlayerState() === 2 || data.target.getPlayerState() === -1) {
      // if (data.target.getPlayerState() === YT.PlayerState.PAUSED || data.target.getPlayerState() === YT.PlayerState.UNSTARTED) {
      this.videoPausado.set(true);
    }

    if (data.target.getPlayerState() === 1) {
      if (this.platformService.isBrowser()) {
        this.videoPausado.set(false);
        this.videoYoutube = document.getElementsByClassName(".ytp-watermark")[0] as HTMLDivElement;

        data.target.setOption("captions", "fontSize", -1);

        if (this.firstTimePlayYT) {
        }
        this.firstTimePlayYT = false;
      }
    }
  }

  /*   savePlayer(player): void {
    this.playerYT = player.target;
    this.firstTimePlayYT = true;
    this.limiteVideo = false;
  }
 */

  /**
   * La función `reproducirVideo` reproduce un vídeo basándose en la URL proporcionada, utilizando
   * diferentes reproductores dependiendo de la plataforma de vídeo.
   * @param url - La URL del vídeo que deseas reproducir.
   * @returns Utilizado para salír de la función en casos no deseados.
   */
  reproducirVideo(url): void {
    if (this.esVideoYoutube(url) && window["player"]) {
      if (this.platformService.isBrowser()) {
        if (!window["player"].playVideo) {
          return;
        }
        window["player"].playVideo();
        this.videoPausado.set(false);
      }
    } else if (this.esDailymotion(url) && window["playerDM"]) {
      if (this.platformService.isBrowser()) {
        if (!window["playerDM"].play) {
          return;
        }
        window["playerDM"].play();
        this.videoPausado.set(false);
      }
    } else {
      if (this.customVideoDiv) {
        this.customVideoDiv.nativeElement.play();
        this.videoPausado.set(false);
      }
    }
  }

  /*   autoplayVideo(initAutoplay): void {
    if (this.isMobile && initAutoplay == 1) {
      setTimeout ( function() {
        this.document.getElementById('play-btn-lottie').click();
      }, 2000);
    }
  } */

  /**
 * La función inicializa un reproductor D
  /**
   * La función `getDailyId` extrae el ID del video de una URL de Dailymotion
   * @param {string} url - Una cadena que representa la URL de un vídeo de Dailymotion.
   * @returns La función `getDailyId` devuelve una cadena si se encuentra una coincidencia en la URL
   * proporcionada; de lo contrario, devuelve `null`.
   */
  getDailyId(url: string): string | null {
    const pattern = /https:\/\/www\.dailymotion\.com\/embed\/video\/(\w+)/;
    const match = url.match(pattern);
    return match && match[1] ? match[1] : null;
  }
  /**
   * La función `getYtId` extrae el ID de un video de YouTube de una URL de YouTube.
   * @param url
   * @returns
   */
  getYtId(url: string): string | null {
    const regex = /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
    return regex.exec(url)[3] || null;
  }

  /**
   * La función "pauseVideo" pausa el reproductor de video según el tipo de plataforma de video (YouTube,
   * Dailymotion o video personalizado).
   */
  public pauseVideo() {
    if (this.esVideoYoutube(this._noticia()?.urlVideo) && this.platformService.isBrowser()) {
      window["player"].pauseVideo();
    }

    if (this.esDailymotion(this._noticia()?.urlVideo) && this.platformService.isBrowser()) {
      window["playerDM"].pause();
    }

    if (!this.esVideoYoutube(this._noticia()?.urlVideo) && !this.esDailymotion(this._noticia()?.urlVideo)) {
      if (this.customVideoDiv) {
        this.customVideoDiv.nativeElement.pause();
      }
    }
  }
  detectarTipo() {
    if (this.esVideoYoutube(this._noticia()?.urlVideo)) {
      this.videoType = "youtube";
    } else if (this.esDailymotion(this._noticia()?.urlVideo)) {
      this.videoType = "dailymotion";
    } else {
      this.videoType = "custom";
    }
  }

  /**
   * La función "customVideoPause" pausa un video personalizado.
   */
  customVideoPause(): void {
    this.videoPausado.set(true);
  }

  customVideoPlay(): void {
    this.videoPausado.set(false);
  }

  closeAnimation(): void {
    this.reproducirVideo(this._noticia()?.urlVideo);
  }
}
